'use client'

import React, { createContext, useContext, ReactNode, useRef, useCallback } from 'react';
import { appApi, AppApiClient } from '@/infrastructure/api';
import { useAuth } from '@/domains/auth';

interface ApiContextType {
  api: AppApiClient;
  isConnected: boolean;
  connectionStatus: 'connected' | 'disconnected' | 'connecting' | 'error';
  checkConnection: () => Promise<void>;
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

interface ApiProviderProps { children: ReactNode; config?: any }

export function ApiProvider({ children, config }: ApiProviderProps) {
  const [isConnected, setIsConnected] = React.useState(false);
  const [connectionStatus, setConnectionStatus] = React.useState<ApiContextType['connectionStatus']>('disconnected');
  const api = React.useMemo(() => (config ? new AppApiClient(config) : appApi), [config]);

  // Employee/JWT path uses useAuth; client cookie path often never sets isInitialized.
  // Health is public on QTP — do not block forever waiting for employee auth.
  const { isInitialized: isAuthInitialized } = useAuth();
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);

  const lastHealthCheckRef = useRef<number>(0);
  const HEALTH_CHECK_THROTTLE = 5000;

  const checkConnection = useCallback(async () => {
    const now = Date.now();
    if (now - lastHealthCheckRef.current < HEALTH_CHECK_THROTTLE) {
      return;
    }

    lastHealthCheckRef.current = now;
    setConnectionStatus('connecting');
    try {
      const res = await fetch('/api/v1/health', {
        credentials: 'include',
        cache: 'no-store',
        signal: AbortSignal.timeout(3000),
      });
      if (res.ok) {
        setIsConnected(true);
        setConnectionStatus('connected');
        return;
      }
      throw new Error(`health ${res.status}`);
    } catch (error) {
      try {
        const fallback = await fetch('/api/paper-agents', {
          credentials: 'include',
          cache: 'no-store',
          signal: AbortSignal.timeout(3000),
        });
        if (fallback.ok) {
          setIsConnected(true);
          setConnectionStatus('connected');
          return;
        }
      } catch {
        /* both probes failed */
      }
      console.warn('ApiContext: health check failed', error);
      setIsConnected(false);
      setConnectionStatus('error');
    }
  }, [api]);

  React.useEffect(() => {
    // Start after mount; if employee auth initializes later, checks already run.
    // Wait briefly for auth OR start after 2s max so client BFF sessions still connect.
    if (!mounted) return;

    let stopped = false;
    const start = () => {
      if (stopped) return;
      checkConnection();
    };

    const initialTimer = setTimeout(start, isAuthInitialized ? 400 : 1500);
    const intervalId = setInterval(start, 30000);

    return () => {
      stopped = true;
      clearTimeout(initialTimer);
      clearInterval(intervalId);
    };
  }, [checkConnection, isAuthInitialized, mounted]);

  return (
    <ApiContext.Provider value={{
      api,
      isConnected,
      connectionStatus,
      checkConnection
    }}>
      {children}
    </ApiContext.Provider>
  );
}

export function useApiContext(): ApiContextType { const ctx = useContext(ApiContext); if (!ctx) throw new Error('useApiContext must be used within an ApiProvider'); return ctx }
export function useApiClient(): AppApiClient { return useApiContext().api }
export function useConnectionStatus() { const { isConnected, connectionStatus } = useApiContext(); return { isConnected, connectionStatus } }


